// src/utils/motionTokens.js
//
// The single source of truth for every animation on the platform.
// DropCard.jsx and CreatorCard.jsx already consume this file directly
// (entrance stagger, press feedback) — everything below is written to
// match what those two components actually import, so this stays the
// real contract rather than a second, competing version of it.
//
// Grounded in the motion research done for the Discovery rebuild —
// NN/g's 100-500ms duration guidance, Apple HIG's "quick, precise,
// intentional" principle, and the refined 2026 tween-vs-spring rule:
// plain duration/ease tweens for anything PROGRAMMATIC (entrances,
// the ring filling, number ticking), a near-zero-bounce spring only
// for anything the user directly initiated (a tap/press). Never the
// reverse — a bouncy spring on page load is the single most common
// motion mistake in the research, and exactly what "no bouncy/
// springy animation" was written to rule out from the start.

import { useReducedMotion } from "framer-motion";

// ── Durations (seconds — Framer Motion's `transition.duration` unit) ──
// Named by what they're for, not by a generic size label, since
// that's the convention DropCard/CreatorCard already use
// (DURATION.entrance). Add a new named key here if a future
// animation genuinely needs a different duration — don't repurpose
// an existing one for something it wasn't tuned for.
export const DURATION = {
  micro: 0.15,     // hover/press feedback that isn't using the spring preset below
  entrance: 0.3,   // card scroll-entrance (DropCard/CreatorCard)
  crossfade: 0.3,  // skeleton -> real content swap
  ring: 0.5,       // the views-remaining ring filling — the one "big movement"
                    // allowed to run slightly longer, per NN/g's guidance that
                    // 400-500ms is acceptable ONLY for larger, less-frequent
                    // movements, never for anything that repeats often.
  fogClear: 0.6,   // DropView glass frost → clear on successful unlock
  sheet: 0.35,     // DropView bottom-sheet open/close tween
};

// ── Easing ───────────────────────────────────────────────────────
// Entrances decelerate into place; exits accelerate away. Never use
// linear. Never use a curve whose y control point exceeds 1 — that
// produces overshoot/bounce, which belongs to the spring preset
// below, never to a tween.
export const EASE = {
  out: [0, 0, 0.2, 1],     // entrances - Material's standard decelerate curve
  in: [0.4, 0, 1, 1],      // exits - accelerate away
  inOut: [0.4, 0, 0.2, 1], // on-screen state changes (a value updating in place)
};

// CSS string equivalents, for anywhere a plain CSS transition is used
// instead of Framer Motion (e.g. the search bar focus ring).
export const EASE_CSS = {
  out: "cubic-bezier(0, 0, 0.2, 1)",
  in: "cubic-bezier(0.4, 0, 1, 1)",
  inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
};

// ── Stagger ──────────────────────────────────────────────────────
// DropCard/CreatorCard each compute their own entrance delay from an
// `index` prop passed down at the map() call site
// (Math.min(index, STAGGER_CAP) * STAGGER_DELAY) rather than using a
// parent-level Framer "staggerChildren" variant — keep that pattern
// for any new card type, don't mix in a second stagger mechanism.
export const STAGGER_DELAY = 0.08; // seconds per card
export const STAGGER_CAP = 8;      // cap so far-down cards in a long grid
                                    // don't queue behind a long delay

// ── Press feedback (user-initiated only) ──────────────────────────
// A near-zero-bounce spring — a touch of overshoot reads as physical
// for anything the user's own tap/click directly caused, per the 2026
// motion-design cross-reference. Kept subtle (bounce: 0, small scale)
// since this is a premium/editorial brand, not a playful consumer app.
export const tapPress = { scale: 0.97 };
export const tapPressTransition = { type: "spring", stiffness: 400, damping: 35, bounce: 0 };

// ── Reduced motion ───────────────────────────────────────────────
// Mandatory, not optional — NN/g and Apple both treat this as a
// baseline requirement. Wraps Framer Motion's own hook into the
// { prefersReduced } shape DropCard/CreatorCard already destructure,
// so every component imports this one hook instead of reaching into
// "framer-motion" directly at each call site.
export function useMotionSafe() {
  const prefersReduced = useReducedMotion();
  return { prefersReduced };
}

// ── The one rule that doesn't live in code ────────────────────────
// Every animated number, ring, or "live" indicator on this platform
// must reflect real data. Never fake an increment to simulate
// activity — that's the exact manufactured-social-proof pattern the
// FTC's dark-patterns guidance flags, not a "cute" liveness trick.
//
// And: no motion that adapts or personalizes per user. Same
// animation behavior for everyone, on principle — same as every
// other part of Discovery's no-algorithm design.